import {Helmet} from '@dr.pogodin/react-helmet';
import {isFeatureEnabled} from 'feature-flags';
import {Action, DataStatus, DropdownActionMenuItem} from 'platform/components';

import {useLocation} from 'react-router-dom';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useListPermissionExpressionQuery} from '@dms/api/security';
import {useGetVehicleWarehouseManagementSettingsQuery} from '@dms/api/warehouse';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds, vehiclesRoutes} from '@dms/routes';
import {Main, PageTab, permissions, usePermissions} from '@dms/shared';

import {buildArray, composePath, useNavigate} from 'shared/web';

import {AdvertisedVehicles} from './(sections)/AdvertisedVehicles/AdvertisedVehicles';
import {OSSaleVehicles} from './(sections)/OSSaleVehicles/OSSaleVehicles';
import {OSServiceVehicles} from './(sections)/OSServiceVehicles/OSServiceVehicles';
import {OSVehicles} from './(sections)/OSVehicles/OSVehicles';
import {RentalVehicles} from './(sections)/RentalVehicles/RentalVehicles';
import {VehiclesOverview} from './(sections)/VehiclesOverview/VehiclesOverview';
import {VehicleWarehouses} from './(sections)/VehicleWarehouses/VehicleWarehouses';

export function Vehicles() {
  const navigate = useNavigate();
  const location = useLocation();

  const {data: vehicleWarehouseSettings, isLoading} =
    useGetVehicleWarehouseManagementSettingsQuery();

  const [
    canPutVehicleUpForSale,
    canPutVehicleUpToBuy,
    canReadBusinessCase,
    canReadWarehouseInfo,
    canCreateRentalVehicle,
    canReadServiceCase,
    canReadRentalVehicle,
  ] = usePermissions({
    permissionKeys: [
      'vehiclePutUpForSale',
      'vehiclePutUpToBuy',
      'businessCaseRead',
      'vehicleReadWarehouseInformation',
      'createRentalVehicle',
      'serviceCaseRead',
      'readRentalVehicle',
    ],
  });

  // TODO: T20-56667 [FE] consider creating isNotDisallowed function/hook for permissions evaluation
  const {data: userPermissions} = useListPermissionExpressionQuery();

  const canCreateSaleVehicle = canPutVehicleUpForSale && canPutVehicleUpToBuy;

  const isVehicleWarehouseEnabled =
    isFeatureEnabled(featureFlags.SALES_VEHICLE_WAREHOUSE_MANAGEMENT) &&
    vehicleWarehouseSettings?.isEnabled;

  const hasSalesLicense = canReadBusinessCase;

  const hasServiceCaseLicense = canReadServiceCase;

  const isWarehousesPage = location.pathname === vehiclesRoutes.vehicleWarehouses;

  const readAdvertisingSettingsPermission = userPermissions?.actions?.find(
    ({actionId, resourceId}) =>
      actionId === permissions.readAdvertisingSettings.actionId &&
      resourceId === permissions.readAdvertisingSettings.resourceId
  );

  const isReadAdvertisingSettingsAvailable =
    isNotNilOrEmpty(readAdvertisingSettingsPermission) &&
    readAdvertisingSettingsPermission!.permissionsExpression.result !== 'DISALLOW';

  const isOSVehicleShowedAndNotSwapped: boolean =
    isFeatureEnabled(featureFlags.SALES_360_VEHICLES) &&
    !isFeatureEnabled(featureFlags.REPLACE_VEHICLE_DG_WITH_OPENSEARCH);

  const tabs = buildArray<PageTab>()
    .when(isOSVehicleShowedAndNotSwapped, {
      id: 'vehicle-opensearch',
      title: i18n.t('page.vehicles.360vehicleView.title'),
      href: vehiclesRoutes.vehicleOpenSearch,
      content: <OSVehicles />,
    })
    .when(hasSalesLicense && !isOSVehicleShowedAndNotSwapped, {
      id: 'all',
      title: i18n.t('page.vehicles.title'),
      href: vehiclesRoutes.home,
      content: <VehiclesOverview />,
    })
    .when(hasSalesLicense && isReadAdvertisingSettingsAvailable, {
      id: 'inAdvertisement',
      title: i18n.t('page.vehicles.inAdvertisement.title'),
      href: vehiclesRoutes.inAdvertisement,
      content: <AdvertisedVehicles />,
    })
    .when(canReadWarehouseInfo && isVehicleWarehouseEnabled && hasSalesLicense, {
      id: 'vehicleWarehouses',
      title: i18n.t('page.vehicles.vehicleWarehouses.title'),
      href: vehiclesRoutes.vehicleWarehouses,
      content: <VehicleWarehouses />,
    })
    .when(isFeatureEnabled(featureFlags.SALES_360_VEHICLES) && !isOSVehicleShowedAndNotSwapped, {
      id: 'vehicle-opensearch',
      title: i18n.t('page.vehicles.360vehicleView.title'),
      href: vehiclesRoutes.vehicleOpenSearch,
      content: <OSVehicles />,
    })
    .when(isOSVehicleShowedAndNotSwapped, {
      id: 'all',
      title: i18n.t('page.vehicles.title'),
      href: vehiclesRoutes.home,
      content: <VehiclesOverview />,
    })
    .when(hasSalesLicense && isFeatureEnabled(featureFlags.SALE_VEHICLE_OPENSEARCH), {
      id: 'saleVehicleOpenSearch',
      title: i18n.t('entity.saleVehicle.labels.title'),
      href: vehiclesRoutes.saleVehicleOpenSearch,
      content: <OSSaleVehicles />,
    })
    .when(hasServiceCaseLicense, {
      id: 'serviceVehicleOpenSearch',
      title: i18n.t('entity.serviceVehicle.labels.title'),
      href: vehiclesRoutes.serviceVehicleOpenSearch,
      content: <OSServiceVehicles />,
    })
    .when(canReadRentalVehicle, {
      id: 'rentalVehicleOverview',
      title: i18n.t('page.rentalOrder.labels.rentalVehicles'),
      href: vehiclesRoutes.rentalVehicleOverview,
      content: <RentalVehicles />,
    });

  const actions = buildArray<Action>()
    .when(isVehicleWarehouseEnabled && isWarehousesPage, {
      type: 'button',
      title: i18n.t('entity.vehicleWarehouse.labels.warehouseMovements'),
      variant: 'outlined',
      onClick: () => {
        navigate(vehiclesRoutes.warehouseMovements);
      },
      'data-testid': 'warehouseMovements',
    })
    .when(hasServiceCaseLicense, {
      type: 'dropdown-button',
      title: i18n.t('entity.vehicle.labels.createVehicle'),
      variant: 'primary',
      menuItems: buildArray<DropdownActionMenuItem>()
        .when(hasSalesLicense, {
          label: i18n.t('entity.vehicle.labels.createSaleVehicle'),
          onClick: () => {
            navigate(vehiclesRoutes.createV2);
          },
          leftIcon: 'maps/directions_car',
          isDisabled: !canCreateSaleVehicle,
        })
        .when(hasServiceCaseLicense, {
          leftIcon: 'action/build',
          label: i18n.t('entity.vehicle.labels.createServiceVehicle'),
          onClick: () => {
            navigate(vehiclesRoutes.createServiceVehicleV2);
          },
        })
        .when(canReadRentalVehicle && canCreateRentalVehicle, {
          leftIcon: 'automotive/rental',
          label: i18n.t('entity.vehicle.labels.createRentalVehicle'),
          onClick: () => {
            navigate(
              composePath(vehiclesRoutes.createRentalVehicleV2, {
                params: {
                  tab: 'basic-information',
                },
              })
            );
          },
        }),
      'data-testid': 'createVehicle',
    })
    .when(hasSalesLicense && !hasServiceCaseLicense, {
      type: 'button',
      title: i18n.t('entity.vehicle.labels.createVehicle'),
      variant: 'primary',
      onClick: () => {
        navigate(vehiclesRoutes.createV2);
      },
      'data-testid': 'createVehicle',
    });

  return (
    <>
      <Helmet title={i18n.t('page.vehicles.title')} />
      <DataStatus isLoading={isLoading} minHeight={100}>
        <Main isFullHeight data-testid={testIds.vehicles.home()} actions={actions} tabs={tabs} />
      </DataStatus>
    </>
  );
}
